'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { useCheckoutStore, BTC_AMOUNT, USD_AMOUNT } from '../../store/checkoutStore';

// Dynamically import QRCode to avoid SSR issues
const QRCode = dynamic(() => import('qrcode.react').then((m) => m.QRCodeSVG), { ssr: false });

// BACKEND INTEGRATION POINT: Replace WALLET_ADDRESS with environment variable
// process.env.NEXT_PUBLIC_BTC_WALLET_ADDRESS
const WALLET_ADDRESS = 'bc1qt3lsryd0gqq8ylayh69l6mn472520xf70t9j4z';

export default function PaymentInstructionsStep() {
  const { setStep } = useCheckoutStore();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const btcUri = `bitcoin:${WALLET_ADDRESS}?amount=${BTC_AMOUNT}&label=LuminaryXPro`;

  return (
    <div className="flex flex-col gap-4 animate-slide-up">
      {/* Amount banner */}
      <div className="rounded-xl p-3 sm:p-4 flex items-center justify-between"
        style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Send exactly</p>
          <div className="flex items-baseline gap-2">
            <span className="font-mono-data text-xl sm:text-2xl font-bold text-primary">{BTC_AMOUNT}</span>
            <span className="text-sm font-semibold text-muted-foreground">BTC</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">≈ ${USD_AMOUNT} USD · Bitcoin Mainnet only</p>
        </div>
        <div className="text-right">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
            <span className="text-primary font-black text-lg sm:text-xl">₿</span>
          </div>
        </div>
      </div>

      {/* QR + address */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        {/* QR Code — smaller on mobile */}
        <div className="flex-shrink-0 flex items-center justify-center p-2 sm:p-3 rounded-xl bg-white self-center sm:self-start">
          <QRCode
            value={btcUri}
            size={96}
            bgColor="#ffffff"
            fgColor="#0a0b0f"
            level="M"
          />
        </div>

        {/* Address */}
        <div className="flex flex-col gap-2 sm:gap-3 flex-1 min-w-0">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">BTC Wallet Address</p>
            <div className="flex items-center gap-2 p-2.5 sm:p-3 rounded-xl"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
              <span className="font-mono-data text-xs text-foreground break-all flex-1 leading-relaxed">
                {WALLET_ADDRESS}
              </span>
              <button
                onClick={() => copyToClipboard(WALLET_ADDRESS, 'address')}
                className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 hover:bg-border"
                aria-label="Copy wallet address"
              >
                {copiedField === 'address' ? (
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Copy amount */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Exact Amount</p>
            <div className="flex items-center gap-2 p-2.5 sm:p-3 rounded-xl"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
              <span className="font-mono-data text-sm text-primary font-semibold flex-1">{BTC_AMOUNT} BTC</span>
              <button
                onClick={() => copyToClipboard(BTC_AMOUNT, 'amount')}
                className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 hover:bg-border"
                aria-label="Copy BTC amount"
              >
                {copiedField === 'amount' ? (
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions — compact on mobile */}
      <div className="rounded-xl p-3 sm:p-4 flex flex-col gap-2"
        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold text-foreground uppercase tracking-wider">Important</p>
        <ul className="flex flex-col gap-1.5">
          {[
            'Send the exact amount shown — partial payments cannot be verified',
            'Bitcoin Mainnet only — do not send from Lightning, Testnet, or other networks',
            'Transaction typically confirms within 10–30 minutes',
            'After sending, copy your transaction hash from your wallet or a BTC explorer',
          ].map((item, i) => (
            <li key={`instruction-${i}`} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="text-primary mt-0.5 flex-shrink-0">·</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* CTA — sticky feel at bottom */}
      <button
        onClick={() => setStep('awaiting_hash')}
        className="btn-primary w-full py-3.5 sm:py-4 text-sm sm:text-base font-bold flex items-center justify-center gap-2"
      >
        I&apos;ve sent the payment
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}